import { Inject, Injectable } from '@angular/core';
import {
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable } from 'rxjs';
import { HeaderType } from 'projects/sharing/general/enums/http-headers.enum';
import {
  MenuActionPair,
  PermissionContextService,
  isBackendApiRequest,
  normalizeRequestEndpoint,
  normalizeRoutePath,
} from 'projects/sharing/general/services/permission-context.service';
import {
  MENU_ACTION_HEADER_CONFIG,
  MENU_ACTION_HEADER_FALLBACK,
  MenuActionHeaderRule,
} from './menu-action-header.config';

type ResolvedMenuActionHeader = Record<string, string>;

@Injectable()
export class MenuActionHeaderInterceptor implements HttpInterceptor {
  constructor(
    private readonly permissionContextService: PermissionContextService,
    private readonly router: Router,
    @Inject(MENU_ACTION_HEADER_CONFIG)
    private readonly config: MenuActionHeaderRule[],
  ) {}

  intercept(
    request: HttpRequest<unknown>,
    next: HttpHandler,
  ): Observable<HttpEvent<unknown>> {
    if (!isBackendApiRequest(request.url)) {
      return next.handle(request);
    }

    const endpointPath = normalizeRequestEndpoint(request.url);

    if (hasCallerPermissionHeaders(request)) {
      return next.handle(request);
    }

    const resolvedHeader = this.resolveHeaders(endpointPath);
    return next.handle(request.clone({ setHeaders: resolvedHeader }));
  }

  /**
   * The interceptor is the single writer of the permission headers. A menu
   * that explicitly registers the endpoint wins; next come the explicit page
   * rules, so endpoints like logout always use their own menu instead of the
   * page the user happens to be idle on; the menu's contextual fallback comes
   * after that, and the login pair is the last resort — for calls that run
   * outside the menu tree (auth pages and the bootstrap calls fired right
   * after login), and for endpoints the contextual fallback has no action id
   * to name, which would otherwise reach the backend as a placeholder.
   */
  private resolveHeaders(endpointPath: string): ResolvedMenuActionHeader {
    const resolved = this.permissionContextService.resolveForRequest(
      this.router.url,
      endpointPath,
    );

    if (resolved?.matched) {
      return createHeader(resolved.menuId, resolved.actionId);
    }

    const rule = this.resolveFromConfig(endpointPath);
    if (rule && (rule.menuId || rule.actionId)) {
      return createHeader(rule.menuId, rule.actionId);
    }

    const pair =
      resolved && isRealHeaderValue(resolved.actionId)
        ? resolved
        : this.permissionContextService.getLoginPair();
    return createHeader(pair.menuId, pair.actionId);
  }

  private resolveFromConfig(endpointPath: string): MenuActionPair | null {
    const routePath = normalizeRoutePath(this.router.url);
    const rule = this.config.find((item) =>
      ruleMatches(item, routePath, endpointPath),
    );
    return rule ? { menuId: rule.menuId, actionId: rule.actionId } : null;
  }
}

function createHeader(
  menuId: string | null | undefined,
  actionId: string | null | undefined,
): ResolvedMenuActionHeader {
  return {
    [HeaderType.X_MENU_ID]: sanitizeHeader(menuId),
    [HeaderType.X_ACTION_ID]: sanitizeHeader(actionId),
  };
}

function sanitizeHeader(value: string | null | undefined): string {
  const sanitized = (value ?? '').trim();
  return sanitized || MENU_ACTION_HEADER_FALLBACK;
}

/**
 * A caller that holds the full pair has already scoped the request to a menu,
 * so the pair is left untouched. Both ids are required: a menu id without an
 * action id would otherwise reach the backend half-filled, and the interceptor
 * is the only place that can complete it.
 */
function hasCallerPermissionHeaders(request: HttpRequest<unknown>): boolean {
  return (
    isRealHeaderValue(request.headers.get(HeaderType.X_MENU_ID)) &&
    isRealHeaderValue(request.headers.get(HeaderType.X_ACTION_ID))
  );
}

function isRealHeaderValue(value: string | null | undefined): boolean {
  const trimmed = (value ?? '').trim();
  return Boolean(trimmed) && trimmed !== MENU_ACTION_HEADER_FALLBACK;
}

function ruleMatches(
  rule: MenuActionHeaderRule,
  routePath: string,
  endpointPath: string,
): boolean {
  if (!endpointListMatches(rule.endpointPaths, endpointPath)) {
    return false;
  }

  return !rule.pagePaths?.length || pageListMatches(rule.pagePaths, routePath);
}

function endpointListMatches(
  endpointPaths: string[] | undefined,
  endpointPath: string,
): boolean {
  return (endpointPaths ?? []).some((item) =>
    endpointMatches(endpointPath, item),
  );
}

function endpointMatches(
  requestEndpoint: string,
  configuredEndpoint: string,
): boolean {
  const endpoint = normalizeRequestEndpoint(configuredEndpoint);
  return (
    endpoint === requestEndpoint ||
    endpointTemplateMatches(requestEndpoint, endpoint)
  );
}

function endpointTemplateMatches(
  requestEndpoint: string,
  template: string,
): boolean {
  const requestSegments = splitEndpoint(requestEndpoint);
  const templateSegments = splitEndpoint(template);
  return (
    requestSegments.length === templateSegments.length &&
    templateSegments.every((item, index) =>
      segmentMatches(requestSegments[index], item),
    )
  );
}

function segmentMatches(segment: string, templateSegment: string): boolean {
  return segment === templateSegment || /^\{[^/{}]+\}$/.test(templateSegment);
}

function splitEndpoint(endpoint: string): string[] {
  return endpoint.split('/').filter(Boolean);
}

function pageListMatches(pagePaths: string[], routePath: string): boolean {
  return pagePaths.some((item) => {
    const pagePath = normalizeRoutePath(item);
    return routePath === pagePath || routePath.startsWith(`${pagePath}/`);
  });
}
